import React from 'react';
import { BloodworkAnalysis, FurtherConsiderations, Section } from '../../models';
import {
  HOLOBIOME_KITS,
  HolobiomeKit,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import { SECTION_HEADINGS } from './supplements-summary-panel';

// Short section labels for the "from:" links under a recommended product —
// matches the compact labels the Results Overview buttons use (e.g. "Immune",
// "Glucose") rather than the long SECTION_HEADINGS labels, which are too wide
// for a 255px kit card. Falls back to the long label for any section not listed.
const SECTION_SHORT_LABEL: Partial<Record<Section, string>> = {
  [Section.RBC]: 'Blood Oxygen',
  [Section.IRON]: 'Iron',
  [Section.WBC]: 'Immune',
  [Section.PLATELETS]: 'Blood Clotting',
  [Section.GLUCOSE]: 'Glucose',
  [Section.KIDNEY]: 'Kidney',
  [Section.PROTEINS]: 'Proteins',
  [Section.NUTRIENTS]: 'Vitamins & Minerals',
  [Section.ELECTROLYTES]: 'Electrolytes',
  [Section.LIVER_BILIARY]: 'Liver',
  [Section.LIPIDS]: 'Lipids',
  [Section.INFLAMMATION_OXIDATIVE_STRESS]: 'Inflammation',
  [Section.THYROID]: 'Thyroid',
  [Section.STRESS_ADRENAL]: 'Stress & Adrenal',
  [Section.SEX_HORMONE]: 'Hormones',
  [Section.PROSTATE]: 'Prostate',
};

// Canonical section order, taken from the Supplements panel's ordering so the
// "from:" links read top-to-bottom the way the report is laid out.
const SECTION_ORDER: Section[] = SECTION_HEADINGS.map(h => h.section);
const sectionShortLabel = (
  section: Section,
): string =>
  SECTION_SHORT_LABEL[section] ??
  SECTION_HEADINGS.find(h => h.section === section)?.label ??
  String(section);

// Fallback Systemic Formulas product page for a Holobiome product whose real
// URL isn't in the supplement catalog yet. The preferred source is the catalog
// link (furtherConsiderations.supplementLinkByName), which carries the sheet's
// exact URL; this slug guess only runs when a product hasn't been wired into the
// catalog. The micro sign is URL-encoded to %c2%b5 (not folded to "u") to match
// the site's own scheme — e.g. "Terra µBiomic" → terra-%c2%b5biomic.
const sfProductUrlFallback = (name: string): string =>
  `https://systemicformulas.com/product/${name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[µμ]/g, '%c2%b5')
    .replace(/[^a-z0-9%-]+/g, '-')
    .replace(/(^-|-$)/g, '')}/`;

// Resolve a product's "learn more" URL: the catalog link from the Supplements
// tab of the sheet first, the slug guess only as a last resort.
const productUrl = (
  name: string,
  furtherConsiderations: FurtherConsiderations,
): string =>
  furtherConsiderations.supplementLinkByName(name) ?? sfProductUrlFallback(name);

// The Holobiome Roadmap is a Systemic Formulas protocol, so the whole panel —
// kits AND add-ons — is scoped to that brand. Another brand's products (e.g.
// BWH Labs' Beta Glucan) are recommendations, but they're not add-ons TO this
// roadmap, so they belong to the Supplements panel only.
const SYSTEMIC_FORMULAS_BRAND = 'systemicFormulas';

// True when the practitioner's Supplement Settings have Systemic Formulas
// checked. An undefined list means no brand filter is in play at all (the
// callers that don't pass one), which leaves the brand enabled.
export function isSystemicFormulasEnabled(brands: string[] | undefined): boolean {
  return !brands || brands.includes(SYSTEMIC_FORMULAS_BRAND);
}

// A product some identified pattern recommends, with everything the panel needs
// to render it: the display name (as resolveSupplement returns it — the master
// supplement title, spelled the same way HOLOBIOME_KITS spells its products),
// the catalog description/link, the section(s) it came from, and the brand(s)
// whose rows recommended it.
export interface RecommendedProduct {
  name: string;
  description: string;
  // The sheet's own URL for the product, when it carries one. A kit product can
  // fall back to a Systemic Formulas slug guess; an add-on only links when the
  // sheet carries a real URL.
  link?: string;
  sections: Section[];
  brands: string[];
}

// Map each recommended supplement (by normalized display name) to the product
// and the sections whose identified patterns recommend it. Same traversal the
// Supplements panel uses (possibleRootCauseCategoriesFor → Supplements
// considerations → resolveSupplement); a product recommended through either its
// primary or its "-2" (section-specific) tag resolves to the same name, and a
// product can be recommended by patterns in more than one section (e.g. EnerGenic
// from both Immune and Glucose), which is exactly why each origin is listed.
export function collectRecommendedProducts(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, RecommendedProduct> {
  const collected = new Map<
    string,
    {
      name: string;
      description: string;
      link?: string;
      sections: Set<Section>;
      brands: Set<string>;
    }
  >();
  SECTION_HEADINGS.forEach(({ section }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    categories.forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        (consideration.items || []).forEach(item => {
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed?.name) return;
          const key = normalizeSupplementName(parsed.name);
          if (!collected.has(key)) {
            collected.set(key, {
              name: parsed.name,
              description: parsed.description || '',
              link: parsed.link,
              sections: new Set(),
              brands: new Set(),
            });
          }
          collected.get(key)!.sections.add(section);
          if (consideration.brand) {
            collected.get(key)!.brands.add(consideration.brand);
          }
        });
      });
    });
  });
  const ordered = new Map<string, RecommendedProduct>();
  collected.forEach((entry, key) =>
    ordered.set(key, {
      name: entry.name,
      description: entry.description,
      link: entry.link,
      sections: SECTION_ORDER.filter(s => entry.sections.has(s)),
      brands: Array.from(entry.brands),
    }),
  );
  return ordered;
}

// Thin wrapper: normalized name → the section(s) that recommend it.
export function collectRecommendedSupplementSections(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, Section[]> {
  const sections = new Map<string, Section[]>();
  collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
  ).forEach((product, key) => sections.set(key, product.sections));
  return sections;
}

// Thin wrapper: just the set of recommended supplement names (used for
// visibility and by tests).
export function collectRecommendedSupplementNames(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Set<string> {
  return new Set(
    collectRecommendedSupplementSections(
      bloodworkAnalysis,
      furtherConsiderations,
      brands,
    ).keys(),
  );
}

// Every product that belongs to a Holobiome kit, normalized — the membership
// test that splits recommendations into "on the roadmap" (a highlighted kit box)
// and "add-on" (the table below it).
const KIT_PRODUCT_KEYS = new Set(
  HOLOBIOME_KITS.flatMap(kit => kit.productNames.map(normalizeSupplementName)),
);

// The Systemic Formulas recommendations that AREN'T in any kit — products a
// pattern identified for this test calls for that the roadmap itself doesn't
// cover (e.g. a section-specific product). Other brands are dropped: they're
// recommendations, but not add-ons to a Systemic Formulas roadmap. Ordered by
// the first section that recommends them so the table reads top-to-bottom the
// way the report is laid out, then alphabetically within a section.
export function collectAddOnProducts(
  recommended: Map<string, RecommendedProduct>,
): RecommendedProduct[] {
  const addOns: RecommendedProduct[] = [];
  recommended.forEach((product, key) => {
    if (KIT_PRODUCT_KEYS.has(key)) return;
    // A brandless row is legacy data, not another brand — the sheet's Supplements
    // rows all carry one, so treat a missing brand as Systemic Formulas.
    const isSystemicFormulas =
      product.brands.length === 0 ||
      product.brands.includes(SYSTEMIC_FORMULAS_BRAND);
    if (isSystemicFormulas) addOns.push(product);
  });
  const firstSectionIdx = (product: RecommendedProduct): number => {
    const idx = SECTION_ORDER.indexOf(product.sections[0]);
    return idx === -1 ? SECTION_ORDER.length : idx;
  };
  return addOns.sort(
    (a, b) =>
      firstSectionIdx(a) - firstSectionIdx(b) || a.name.localeCompare(b.name),
  );
}

// Count of kit products that are active for the given recommendation set. The
// panel only has something to show when at least one recommended supplement
// falls inside a kit, so this doubles as the visibility signal.
function activeKitProductCount(activeNames: Set<string>): number {
  let count = 0;
  HOLOBIOME_KITS.forEach(kit => {
    kit.productNames.forEach(name => {
      if (activeNames.has(normalizeSupplementName(name))) count += 1;
    });
  });
  return count;
}

export function hasSupplementProtocolsContent(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): boolean {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return false;
  // The roadmap is a Systemic Formulas protocol — unchecking that brand in
  // Supplement Settings hides the whole panel, not just its products.
  if (!isSystemicFormulasEnabled(brands)) return false;
  const active = collectRecommendedSupplementNames(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
  );
  return activeKitProductCount(active) > 0;
}

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Jump to a report section. When omitted, the "from:" section origins render
  // as plain text instead of links (e.g. in the PDF).
  onSectionClick?: (section: Section) => void;
}

// Mix a 6-digit hex toward white — 0 keeps the color, 1 is white. Lets each kit
// derive its pale row tint and muted accent from its single color, the way the
// Results Overview derives STATUS_TINT from its status palette. Solid 6-digit
// output (no rgba) so it rasterizes cleanly through html2canvas in the PDF.
const mixWithWhite = (hex: string, amount: number): string => {
  const value = parseInt(hex.replace('#', ''), 16);
  const channel = (shift: number): string => {
    const c = (value >> shift) & 0xff;
    return Math.round(c + (255 - c) * amount)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(16)}${channel(8)}${channel(0)}`;
};

// A product row — the Results Overview section-row shape: pale tint, a 4px left
// accent in the kit color, dark readable text, no border. EVERY product ships in
// the kit, so a product no pattern pointed to is still a normal, present row
// (neutral tint, muted accent, full-strength text) rather than a grayed-out one
// that would read as "not needed". A recommended product deepens the tint and
// takes the full-strength accent; its name goes bold in the kit color and its
// section origins print underneath, so the distinction still reads without color.
const productRowStyle = (
  kit: HolobiomeKit,
  recommended: boolean,
): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '6px 9px',
  borderRadius: '6px',
  border: 'none',
  borderLeft: `4px solid ${recommended ? kit.color : mixWithWhite(kit.color, 0.6)}`,
  backgroundColor: recommended ? mixWithWhite(kit.color, 0.87) : '#f7f8f9',
  color: '#212529',
  fontSize: '12px',
});

// The section(s) that recommended a product, each a jump link when
// onSectionClick is provided. A product can be recommended by more than one
// section, so all are listed. Used as line 2 inside an active kit box (white on
// the kit color) and in the Add-On Products table (blue on white).
function SectionOrigins({
  sections,
  onSectionClick,
  color = '#fff',
  style,
}: {
  sections: Section[];
  onSectionClick?: (section: Section) => void;
  color?: string;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        fontSize: '10.5px',
        letterSpacing: 'normal',
        lineHeight: 1.2,
        opacity: 0.95,
        ...style,
      }}
    >
      {sections.map((section, i) => (
        <React.Fragment key={section}>
          {i > 0 && <span> · </span>}
          {onSectionClick ? (
            <span
              role="button"
              tabIndex={0}
              onClick={() => onSectionClick(section)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSectionClick(section);
                }
              }}
              style={{ color, cursor: 'pointer', textDecoration: 'underline' }}
            >
              {sectionShortLabel(section)}
            </span>
          ) : (
            <span>{sectionShortLabel(section)}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// One kit card — the Results Overview group card shape: 255px wide, white, a 4px
// top accent in the kit color, a header divider under a kit-colored title and
// gray subtitle, then a column of section-style rows. The header counts the
// products so the card reads as "the kit contains these N", and every product
// gets a normal row whether or not a pattern pointed to it; the ✓ rows are the
// ones this test's patterns point to, with their section origins as jump links.
function KitCard({
  kit,
  sectionsByName,
  furtherConsiderations,
  onSectionClick,
}: {
  kit: HolobiomeKit;
  sectionsByName: Map<string, Section[]>;
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
}): JSX.Element {
  return (
    <div
      style={{
        flex: '0 0 255px',
        width: '255px',
        border: '1px solid #e6e9ef',
        borderTop: `4px solid ${kit.color}`,
        borderRadius: '8px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
      }}
    >
      <div
        style={{
          borderBottom: '1px solid #eef0f3',
          paddingBottom: '10px',
        }}
      >
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '15px',
            color: kit.color,
            lineHeight: 1.25,
            marginBottom: 2,
          }}
        >
          Month {kit.month}: {kit.name}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: 1.3 }}>
          {kit.subtitle ? `${kit.subtitle} · ` : ''}
          {kit.productNames.length} products
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          gap: '6px',
        }}
      >
        {kit.productNames.map(name => {
          const sections = sectionsByName.get(normalizeSupplementName(name));
          const recommended = !!sections && sections.length > 0;
          return (
            <div key={name} style={productRowStyle(kit, recommended)}>
              <a
                href={productUrl(name, furtherConsiderations)}
                target="_blank"
                rel="noopener noreferrer"
                title={`Learn more about ${name} at Systemic Formulas`}
                style={{
                  color: recommended ? kit.color : '#212529',
                  textDecoration: 'underline',
                  fontWeight: recommended ? 'bold' : 500,
                }}
              >
                {name}
              </a>
              {recommended && sections && (
                <SectionOrigins
                  sections={sections}
                  onSectionClick={onSectionClick}
                  color={kit.color}
                  style={{ fontSize: '11px' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const addOnCellStyle: React.CSSProperties = {
  padding: '5px 8px',
  borderTop: '1px solid #e9ecef',
  verticalAlign: 'top',
};

const addOnHeaderCellStyle: React.CSSProperties = {
  padding: '5px 8px',
  borderBottom: '1px solid #dee2e6',
  textAlign: 'left',
  fontWeight: 'bold',
  color: '#6c757d',
  fontSize: '11px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

// "Add-On Products" — the recommendations that fall outside the roadmap. Same
// data as a highlighted kit box (product, recommending section(s)) plus the
// catalog description, laid out as a table because these products have no kit
// to group them into. Renders nothing when every recommendation is on the
// roadmap.
function AddOnProductsTable({
  addOns,
  furtherConsiderations,
  onSectionClick,
}: {
  addOns: RecommendedProduct[];
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
}): JSX.Element | null {
  if (addOns.length === 0) return null;
  return (
    <div
      style={{
        width: '100%',
        marginTop: '4px',
        borderTop: '1px solid #dee2e6',
        paddingTop: '10px',
      }}
    >
      <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#212529' }}>
        Add-On Products
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          lineHeight: 1.2,
          marginBottom: '8px',
        }}
      >
        Recommended by patterns identified in this test, but not part of the
        Holobiome Roadmap
      </div>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '12px',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            {/* Product and Recommended Section are fixed and narrow;
                Description takes whatever's left (~68%), since it's the only
                long text. */}
            <th style={{ ...addOnHeaderCellStyle, width: '15%' }}>Product</th>
            <th style={addOnHeaderCellStyle}>Description</th>
            <th style={{ ...addOnHeaderCellStyle, width: '17%' }}>
              Recommended Section
            </th>
          </tr>
        </thead>
        <tbody>
          {addOns.map(product => {
            // A kit product can fall back to a guessed Systemic Formulas slug;
            // an add-on can be any brand, so only a real URL (the bullet's own
            // link, or the catalog's) becomes a link.
            const link =
              product.link ?? furtherConsiderations.supplementLinkByName(product.name);
            return (
              <tr key={product.name}>
                <td style={{ ...addOnCellStyle, fontWeight: 'bold', color: '#212529' }}>
                  {link ? (
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Learn more about ${product.name}`}
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      {product.name}
                    </a>
                  ) : (
                    product.name
                  )}
                </td>
                <td style={{ ...addOnCellStyle, color: '#495057' }}>
                  {product.description}
                </td>
                <td style={addOnCellStyle}>
                  <SectionOrigins
                    sections={product.sections}
                    onSectionClick={onSectionClick}
                    color="#007bff"
                    style={{ fontSize: '12px' }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The panel legend. Each kit is bought whole, so a highlighted product means
// "this test's patterns point here", never "the other products are unnecessary
// or left out". Stated once above the cards rather than repeated on each one.
function RoadmapLegend(): JSX.Element {
  return (
    <div
      style={{
        fontSize: '12px',
        color: '#6c757d',
        lineHeight: 1.35,
        marginBottom: '12px',
      }}
    >
      Each month's kit ships with <strong>all</strong> of its products.
      Highlighted products are the ones a pattern identified in this test points
      to, listed with the section it came from.
    </div>
  );
}

// "Supplement Protocols" — a Results-Overview-style row of the five Holobiome
// kits. Every product in a kit is listed as a normal row; the ones a pattern
// identified for this test points to take the kit color, a ✓, and their
// recommending section(s) as jump links, so the practitioner can see at a glance
// which kits the recommendations cluster into and where they came from.
// Recommendations that aren't in any kit are listed in the Add-On Products table
// below the kits, so nothing a pattern called for goes missing.
export const SupplementProtocolsPanel: React.FC<Props> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  onSectionClick,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  // Same brand gate as hasSupplementProtocolsContent, so a caller that renders
  // the panel without checking first still respects the Systemic Formulas
  // checkbox.
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return null;

  const recommended = collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const sectionsByName = new Map<string, Section[]>();
  recommended.forEach((product, key) => sectionsByName.set(key, product.sections));

  return (
    <>
      <RoadmapLegend />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          marginBottom: '16px',
        }}
      >
        {HOLOBIOME_KITS.map(kit => (
          <KitCard
            key={kit.key}
            kit={kit}
            sectionsByName={sectionsByName}
            furtherConsiderations={furtherConsiderations}
            onSectionClick={onSectionClick}
          />
        ))}
      </div>
      <AddOnProductsTable
        addOns={collectAddOnProducts(recommended)}
        furtherConsiderations={furtherConsiderations}
        onSectionClick={onSectionClick}
      />
    </>
  );
};
